import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO, mergeKeywordSets, COMPETITIVE_KEYWORD_CLUSTERS } from '../lib/seo';
import { StockHeroStrip, StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function BestRelationshipApp() {
  useSEO({
    title: 'Best Relationship App for Communication and Conflict Repair',
    description:
      'Discover what to look for in the best relationship app, then compare TENSION features for communication, repair, and long-term relationship health.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.conflict as unknown as string[],
      ['best relationship app', 'relationship app for couples'],
    ),
    canonical: '/best-relationship-app',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 md:py-14">
        <header className="grid gap-6 overflow-hidden rounded-3xl border border-violet-200/60 bg-white/90 shadow-xl md:grid-cols-[0.88fr_1.12fr] md:items-center">
          <div className="relative min-h-[220px] md:order-first md:min-h-[300px]">
            <StockImage
              stockKey="coupleKitchen"
              width={900}
              height={700}
              priority
              alt="Partners sharing a warm moment together indoors"
              className="h-full w-full object-cover md:rounded-none md:rounded-l-3xl"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-l from-white/20 to-transparent md:from-white/40"
              aria-hidden
            />
          </div>
          <div className="p-6 md:p-10">
            <h1 className="text-3xl font-black tracking-tight text-neutral-900 md:text-4xl">
              Best relationship app for calmer, healthier connection
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-neutral-600 md:text-lg">
              The strongest relationship apps combine practical communication support, conflict prevention, and clear
              progress tracking.
            </p>
          </div>
        </header>

        <StockHeroStrip stockKey="coupleOutdoor" alt="Couple enjoying time together outside" />

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="font-semibold text-neutral-900">Communication coaching</h2>
            <p className="mt-2 text-sm text-neutral-700">Draft safer messages and reduce avoidable escalation.</p>
          </Card>
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="font-semibold text-neutral-900">Conflict prevention</h2>
            <p className="mt-2 text-sm text-neutral-700">Identify tension patterns before they become larger ruptures.</p>
          </Card>
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="font-semibold text-neutral-900">Actionable progress</h2>
            <p className="mt-2 text-sm text-neutral-700">Use practical next steps instead of passive insights.</p>
          </Card>
        </div>

        <Card className="border-violet-200/80 bg-gradient-to-r from-violet-50/95 to-fuchsia-50/90 p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-neutral-900">Evaluate TENSION quickly</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md">Compare plans</Button>
            </Link>
            <Link to="/compare/tension-vs-paired">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                Compare with Paired
              </Button>
            </Link>
            <Link to="/best-couples-app">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                Best couples app guide
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </WarmMarketingBackdrop>
  );
}
